import {
    insertCacheItem,
    deleteCacheItem
} from '../actions/cacheActions';

import {
    put
} from 'redux-saga/effects';

import { warn } from '../../../core/js/utils/logger';

/**
 * Does the browser support localStorage
 * @return {Boolean} true if localStorage is supported, false otherwise
 */
export function canUseLocalStorage() {
    const isSupported = (typeof (Storage) !== 'undefined');

    if (!isSupported) {
        warn('localStorage not supported');
        return false;
    }

    let usesLocalStorage = false;

    try {
        // Try adding and removing key to ensure that even though localStorage
        // might be available it is actually functional
        const testSet = 'testSetForCanUse';

        try {
            window.localStorage.setItem(testSet, 'true');
            window.localStorage.removeItem(testSet);
        } catch (error) {
            // Make sure it didn't fail because it was full
            window.localStorage.clear();
            window.localStorage.setItem(testSet, 'true');
            window.localStorage.removeItem(testSet);
        }

        if (window && window.localStorage) {
            usesLocalStorage = true;
        }
    } catch (err) {
        warn('localStorage access is denied');
    }

    return isSupported && usesLocalStorage;
}

/**
 * Get the browser's localStorage object, if it is supported
 * @return {Storage} browser's localStorage object
 */
export function getLocalStorage() {
    if (!canUseLocalStorage()) {
        return undefined;
    }

    return window.localStorage;
}

function createCacheItem(value, ttlInSeconds) {
    const expires = new Date().getTime() + (ttlInSeconds * 1000);

    return {
        value: value,
        expires: expires
    };
}

/**
 * Remove the cached value by the specified key
 * @param  {String} key Key of cache value to remove
 * @return {undefined}
 */
export function removeFromCache(key) {
    if (!canUseLocalStorage()) {
        return;
    }

    const storage = getLocalStorage();

    storage.removeItem(key);
}

function cacheItemHasExpired(cacheItem) {
    if (cacheItem) {
        const now = new Date();
        const cacheExpirationDate = new Date(cacheItem.expires);

        if (now >= cacheExpirationDate) {
            return true;
        }
    }

    return false;
}

/**
 * Get cached value by key
 * @param  {String} key Key of cached value
 * @return {object, array, or undefined}  Cached JSON value, undefined if not found, expired, or localStorage is not supported
 */
export function getCacheValue(key) {
    if (!canUseLocalStorage()) {
        return undefined;
    }

    const storage = getLocalStorage();
    const cacheItemAsString = storage.getItem(key);

    if (!cacheItemAsString) {
        return undefined;
    } else {
        const cacheItem = JSON.parse(cacheItemAsString);

        if (cacheItemHasExpired(cacheItem)) {
            removeFromCache(key);
            return undefined;
        } else {
            return cacheItem.value;
        }
    }
}

export function getReduxCache(state, key) {
    if (!state.cache) {
        return undefined;
    }
    const cacheItem = state.cache[key];

    if (!cacheItem) {
        return undefined;
    } else if (cacheItemHasExpired(cacheItem)) {
        put(deleteCacheItem(key));
        return undefined;
    } else {
        return cacheItem.value;
    }
}

/**
 * Update cached value without modifying its expiration.  No-op if cached value does not exist or has expired.
 * @param  {String} key      Key of item to update
 * @param  {object or array} value new JSON object to cache under the specified key
 * @return {boolean}         true, if value was updated, false otherwise
 */
export function updateCacheValue(key, value) {
    if (!canUseLocalStorage()) {
        return false;
    }

    const storage = getLocalStorage();
    const cacheItemAsString = storage.getItem(key);

    if (cacheItemAsString) {
        const cacheItem = JSON.parse(cacheItemAsString);

        if (!cacheItemHasExpired(cacheItem)) {
            cacheItem.value = value;

            const updatedCacheItemAsString = JSON.stringify(cacheItem);

            setLocalStorageItem(key, updatedCacheItemAsString);
            return true;
        }
    }

    return false;
}

function setLocalStorageItem(key, value, clearIfFull = true) {
    let storage;

    try {
        storage = getLocalStorage();

        if (storage) {
            storage.setItem(key, value);
        }
    } catch (err) {
        if (clearIfFull) {
            storage.clear();
            storage.setItem(key, value);
        }
    }
}

/**
 * Insert new value into localStorage cache, will clobber existing values with new expiration
 * @param {String} key                  Key to insert value under
 * @param {object or array} value       JSON value to cache
 * @param {number} ttlInSeconds         Number of seconds since now for cached value to live
 */
export function setCacheValue(key, value, ttlInSeconds) {
    if (!canUseLocalStorage()) {
        return false;
    }

    const cacheItem = createCacheItem(value, ttlInSeconds);
    const cacheItemAsString = JSON.stringify(cacheItem);

    setLocalStorageItem(key, cacheItemAsString);

    return true;
}

export function setReduxCache(key, value, ttlInSeconds) {
    const cacheItem = createCacheItem(value, ttlInSeconds);

    return cacheItem;
}

/**
 * Clears the browser's localStorage
 * @return {undefined}
 */
export function clearCache() {
    if (!canUseLocalStorage()) {
        return;
    }

    const storage = getLocalStorage();

    storage.clear();
}

/**
 * Create a cache key from a given list of slugs
 * @param  {Array[String]}  slugs List of slugs
 * @return {String}       Cache key
 */
export function getCacheKey(slugs = []) {
    return slugs.reduce(
        (cacheKey, slug) => `${cacheKey}${slug}`,
        ''
    );
}

/**
 * Try to set a cache item in local storage for the default store
 * If the cache item can't be set, set it to a window variable instead
 * @param {string}              key             Cache key
 * @param {obect}               store           MISL store object
 * @param {number}              ttlInSeconds    Time to live for cache object in seconds
 * @param {object|undefined}    defObj          either a mocked window object for tests or undefined
 */
export function setLocalStorageItemWithFallback(key, value, ttlInSeconds, defObj) {
    // setCacheValue will return true if successful, false if not.
    const cacheItemWasSet = setCacheValue(key, value, ttlInSeconds);

    if (!cacheItemWasSet) {
        if (!defObj) {
            // eslint-disable-next-line no-param-reassign
            defObj = window;
        }
        warn(`localStore.setItem() failed. Falling back to saving ${key} in window.${key} instead.`);
        defObj[key] = value;
    }
}

/**
 * Try to get a cache item from local storage for the default store
 * If the cache item isn't found, check window variable instead
 * @param {string}              key     Cache key
 * @param {object|undefined}    defObj  either a mocked window object for tests or undefined
 */
export function getLocalStorageItemWithFallback(key, defObj) {
    if (!defObj) {
        // eslint-disable-next-line no-param-reassign
        defObj = window;
    }
    return getCacheValue(key) || defObj[key];
}

export default {
    get: getCacheValue,
    update: updateCacheValue,
    set: setCacheValue,
    remove: removeFromCache,
    clear: clearCache,
    setReduxCache: setReduxCache,
    getReduxCache: getReduxCache,
    getWithFallback: getLocalStorageItemWithFallback,
    setWithFallback: setLocalStorageItemWithFallback
};
